import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DrillLinkedService, drillAuthTypes } from 'app/model/linkedservice/drill.linkedservice';
import { akvToResource, akvToModel, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from '../../../shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { Constants } from '../../../shared/shared-constants';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext } from 'shell-models-lib';
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DrillLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: DrillLinkedService) => {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        if (ls.authenticationType === drillAuthTypes.NoAuthentication) {
                            return '*';
                        }
                        if (ls.authenticationType === drillAuthTypes.Plain && ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                pwd: 'secret',
                encryptedCredential: '*'
            }
        }
    }
})
export class DrillLinkedServiceAdapter extends LinkedServiceAdapter<DrillLinkedService> {
    protected modelConstructor(): new () => DrillLinkedService {
        return DrillLinkedService;
    }

    protected model(): IModel {
        return DrillLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: DrillLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.DrillLinkedService;
        properties.type = Metamodel.modelFor(DrillLinkedService).name;

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('ConnectionType', 'Direct');
            builder.add('Host', linkedService.host || '');
            builder.add('Port', linkedService.port ? linkedService.port.toString() : '');
            builder.add('AuthenticationType', linkedService.authenticationType || '');
            if (linkedService.authenticationType === drillAuthTypes.Plain) {
                builder.add('UID', linkedService.userName || '');
                if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                    builder.add('PWD', linkedService.password);
                }
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.host === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.authenticationType === undefined &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        properties.typeProperties = {
            connectionString,
            pwd: !linkedService.enableAKV && linkedService.authenticationType === drillAuthTypes.Plain && linkedService.password instanceof AzureKeyVault ?
                akvToResource(linkedService.password, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: DrillLinkedService): Promise<DrillLinkedService> {
        const properties = linkedServiceResource.properties as Model.DrillLinkedService;

        linkedService.host = undefined;
        linkedService.port = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;
        linkedService.authenticationType = undefined;

        if (isString(properties.typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService();
            builder.parse(properties.typeProperties.connectionString as string);

            linkedService.host = builder.getValue('Host', false) || '';
            linkedService.port = parseInt(builder.getValue('Port', false), 10) || undefined;
            linkedService.authenticationType = builder.getValue('AuthenticationType', false) || '';
            linkedService.userName = builder.getValue('UID', false) || '';
            linkedService.password = Constants.maskedCredential;
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Host', 'Port', 'AuthenticationType', 'UID', 'PWD']);
            builder.removeDefaultKeyValuePairs(linkedService.additionalFields, 'ConnectionType', 'Direct');
            if (properties.typeProperties.pwd) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.pwd, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.host = Constants.maskedCredential;
            linkedService.authenticationType = undefined;
            linkedService.userName = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.port = undefined;
            if (properties.typeProperties.pwd) {
                linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.pwd, false, this.context);
            }
        } else if (properties.typeProperties.connectionString
            && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }
        return linkedService;
    }
}
